<script setup lang="ts">
const props = defineProps<{
  variant: SingleProductVariant
}>()

const auth = useAuthorizeStore()

const { data: previews } = useProductPreviews()

const { mutate: enablePreview, isPending: isEnablingPreview } = useEnableProductPreview()

const hrColor = computed(() => props.variant.mainColor || null)

const expiry = computed(() => {
  const raw = previews.value?.[props.variant.sku]
  return raw ? new Date(raw) : null
})

const previewLink = computed(() => {
  if (expiry.value && !isPast(expiry.value)) {
    return props.variant.previewLink
  }

  return null
})

const now = useNow()

const userIsAllowedToPreview = computed(() => {
  if (expiry.value && isBefore(expiry.value, now.value)) {
    // Preview has already been ordered and expired
    return false
  }

  return auth.loggedIn && isTeacher(auth.user) && !isConsumer(auth.user)
})

const contentFlags = computed(() => ({
  digitalbok: !!props.variant.formats?.includes('digitalbok'),
  description: props.variant.isPurchasable && !!props.variant.description,
  preview: userIsAllowedToPreview.value && !!props.variant.previewLink,
}))
</script>

<template>
  <UiCartAddWidget
    v-if="variant.isPurchasable"
    :variant="variant"
    dir="horizontal"
    class="[--padding-inline:1.5rem] [--padding-block:1.5rem]"
  />
  <div v-else class="flex flex-col gap-4 rounded-sm bg-white p-4 [--padding-inline:1.5rem] [--padding-block:1.5rem]">
    <div>
      <div v-if="variant.prices.discount" class="text-base line-through text-gray-40">{{ norwegianPrice(variant.prices.default) }}</div>
      <div class="text-2xl font-bold" :class="{ 'text-coral-30': variant.prices.discount }">{{ norwegianPrice(variant.prices.discount || variant.prices.default) }}</div>
    </div>
    <UButton
      class="rounded-full bg-[#003190] px-legacy-m py-legacy-xs text-center justify-center text-white no-underline"
      :href="`https://www.norli.no/${variant.sku}`"
      target="_blank"
      trailing-icon="far:external-link"
      :ui="{
        trailingIcon: 'size-3.5',
      }"
    >
      Kjøp hos Norli
    </UButton>
    <UButton
      class="rounded-full bg-[#F7EAE0] px-legacy-m py-legacy-xs text-center justify-center text-black no-underline"
      :href="`https://www.pendel.no/${variant.sku}`"
      target="_blank"
      trailing-icon="far:external-link"
      :ui="{
        trailingIcon: 'size-3.5 text-[#FA7F5A]',
      }"
    >
      Kjøp hos Lære
    </UButton>
    <div class="flex flex-col items-center gap-2 border-t pt-4 text-center">
      <template v-if="contentFlags.preview">
        <p class="text-base font-semibold">
          Ønsker du å vurdere boka?
        </p>
        <template v-if="previewLink">
          <p class="leading-normal">
            Du har tilgang til et digitalt vurderingseksemplar av boka.
          </p>
          <a
            :href="previewLink"
            target="_blank"
            rel="noopener"
            class="flex w-fit items-center gap-[0.4rem]"
          >
            Les vurderingseksemplar
            <UIcon name="far:external-link" size="0.75em" />
          </a>
        </template>
        <template v-else>
          <UButton
            class="rounded-full border px-legacy-m py-legacy-xs text-black no-underline"
            @click="enablePreview(variant)"
          >
            Aktiver vurderingseksemplar
          </UButton>
          <p class="text-sm text-gray-40">
            Få gratis tilgang til et digitalt eksemplar i 3 måneder for å vurdere boka til din klasse.
          </p>
        </template>
      </template>

      <LoginLink
        v-else-if="variant.previewLink && !auth.loggedIn"
        class="w-fit text-base underline"
        provider="feide"
        :redirect-to="`${$route.path}#${variant.sku}`"
      >
        Logg inn med FEIDE for å bestille <b>vurderingseksemplar</b>
      </LoginLink>
    </div>
  </div>
</template>
